import { SyncOperation } from './offlineDataTypes';

class SyncEngine {
  private outbox: SyncOperation[] = [];
  private listeners: ((outbox: SyncOperation[]) => void)[] = [];
  private isSyncing = false;

  constructor() {
    this.loadOutbox();
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        this.processOutbox();
      });
    }
  }

  private loadOutbox() {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const saved = localStorage.getItem('yamoopay_sync_outbox');
        if (saved) {
          this.outbox = JSON.parse(saved);
        }
      }
    } catch (e) {
      console.warn('[SyncEngine] Failed to load outbox:', e);
    }
  }

  private persistOutbox() {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem('yamoopay_sync_outbox', JSON.stringify(this.outbox));
      }
    } catch (e) {
      console.warn('[SyncEngine] Failed to persist outbox:', e);
    }
    this.notifyListeners();
  }

  public getOutbox(): SyncOperation[] {
    return [...this.outbox];
  }

  public enqueueOperation(
    operationType: SyncOperation['operationType'],
    payload: any,
    privacyClass: SyncOperation['privacyClass'] = 'PUBLIC'
  ): SyncOperation {
    const op: SyncOperation = {
      id: `sync-op-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
      operationType,
      schemaVersion: 1,
      createdAt: new Date().toISOString(),
      payload,
      privacyClass,
      retryCount: 0,
      maxRetries: 5,
      status: 'QUEUED',
      idempotencyKey: `idem-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`
    };

    this.outbox.unshift(op);
    this.persistOutbox();

    // Trigger immediate process if online
    if (typeof navigator !== 'undefined' && navigator.onLine) {
      this.processOutbox();
    }

    return op;
  }

  public async processOutbox(): Promise<void> {
    if (this.isSyncing || this.outbox.length === 0) return;
    if (typeof navigator !== 'undefined' && !navigator.onLine) return;

    this.isSyncing = true;

    for (const op of this.outbox) {
      if (op.status === 'QUEUED' || op.status === 'FAILED') {
        op.status = 'SYNCING';
        this.persistOutbox();

        try {
          // Simulate deterministic idempotent server processing
          await new Promise((r) => setTimeout(r, 600));

          op.status = 'SYNCED';
        } catch (e: any) {
          op.retryCount++;
          op.errorMessage = e?.message || 'Network sync timeout';
          if (op.retryCount >= op.maxRetries) {
            op.status = 'FAILED';
          } else {
            op.status = 'QUEUED';
          }
        }
      }
    }

    // Keep synced ops for history (up to last 10)
    this.outbox = this.outbox.filter((op, idx) => op.status !== 'SYNCED' || idx < 10);
    this.persistOutbox();
    this.isSyncing = false;
  }

  public clearOutbox() {
    this.outbox = [];
    this.persistOutbox();
  }

  public subscribe(callback: (outbox: SyncOperation[]) => void): () => void {
    this.listeners.push(callback);
    callback(this.outbox);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== callback);
    };
  }

  private notifyListeners() {
    this.listeners.forEach((l) => l(this.outbox));
  }
}

export const syncEngine = new SyncEngine();
